import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = { pro: 'Steel', pro_plus: 'Damascus' };
const TIER_GRADIENTS = {
  Steel:    'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
  Damascus: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
};

// Poll every 2s. Stop on success / explicit failure / max attempts.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s — webhook usually fires in <5s but proxy delays happen.

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [phase, setPhase] = useState('polling'); // 'polling' | 'success' | 'expired' | 'error' | 'unpaid'
  const [tierApplied, setTierApplied] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  const stopTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const pollOnce = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, { withCredentials: true });
      const { payment_status, status, tier_applied } = r.data;

      if (tier_applied) {
        setTierApplied(tier_applied);
        setPhase('success');
        // Refresh user.tier in the auth context so the rest of the UI reflects the upgrade.
        try { await checkAuth(); } catch (err) { console.warn('Post-checkout auth refresh failed:', err); }
        stopTimer();
        return;
      }
      if (status === 'expired') {
        setPhase('expired');
        stopTimer();
        return;
      }
      // Still initiated / open / complete-but-unpaid — keep polling.
      setAttempts((a) => a + 1);
    } catch (err) {
      // 404 = session not found for this user. 403 = not your session.
      const code = err.response?.status;
      if (code === 404 || code === 403) {
        setPhase('error');
        stopTimer();
        return;
      }
      // Transient — keep retrying.
      setAttempts((a) => a + 1);
    }
  }, [sessionId, checkAuth]);

  useEffect(() => {
    if (!sessionId) {
      setPhase('error');
      return;
    }
    if (phase !== 'polling') return;
    if (attempts >= MAX_ATTEMPTS) {
      setPhase('unpaid');
      return;
    }
    // Run immediately on first mount, then every POLL_INTERVAL_MS.
    timerRef.current = setTimeout(pollOnce, attempts === 0 ? 0 : POLL_INTERVAL_MS);
    return stopTimer;
  }, [sessionId, phase, attempts, pollOnce]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!sessionId) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-missing-session">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h1 className="text-2xl font-extrabold text-[#020617]">Missing checkout session.</h1>
        <p className="text-[#64748B] mt-2">We couldn&apos;t find a payment to confirm.</p>
        <Button onClick={() => navigate('/pricing')} className="mt-6" data-testid="checkout-success-back-to-pricing">
          Back to pricing
        </Button>
      </div>
    );
  }

  if (phase === 'polling') {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-polling">
        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <h1 className="text-2xl font-extrabold text-[#020617]">Confirming your payment…</h1>
        <p className="text-[#64748B] mt-2">
          Stripe is letting us know your subscription is live. This usually takes a few seconds.
        </p>
        <p className="text-xs text-[#94A3B8] mt-4">Session: <code>{sessionId.slice(0, 24)}…</code></p>
      </div>
    );
  }

  if (phase === 'success') {
    const tierName = TIER_LABEL[tierApplied] || 'Pro';
    const grad = TIER_GRADIENTS[tierName] || TIER_GRADIENTS.Steel;
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-confirmed">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-wider mb-4" style={{ backgroundImage: grad }} data-testid="checkout-success-tier-badge">
          <Sparkles className="w-3 h-3" /> Welcome to {tierName}
        </div>
        <h1 className="text-3xl font-extrabold text-[#020617] tracking-tight">
          You&apos;re forged in <span style={{ backgroundImage: grad, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{tierName}</span>.
        </h1>
        <p className="text-[#64748B] mt-3">
          {tierName === 'Damascus'
            ? 'Your account is upgraded. Unlimited everything plus a 36-month forecast horizon is now live.'
            : 'Your account is upgraded. Unlimited accounts, recurring transactions, and CSV import + auto-reconcile are now live.'}
        </p>
        <div className="flex gap-3 justify-center mt-6">
          <Link to="/dashboard">
            <Button className="text-white border-0 hover:opacity-90" style={{ backgroundImage: grad }} data-testid="checkout-success-go-dashboard">
              Take me to the dashboard <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-expired">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h1 className="text-2xl font-extrabold text-[#020617]">This checkout expired.</h1>
        <p className="text-[#64748B] mt-2">
          The session timed out before payment finished. No charge was made. Pick a plan again to start over.
        </p>
        <Button onClick={() => navigate('/pricing')} className="mt-6" data-testid="checkout-success-back-after-expired">
          Back to pricing
        </Button>
      </div>
    );
  }

  if (phase === 'unpaid') {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-unpaid">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h1 className="text-2xl font-extrabold text-[#020617]">Still waiting on Stripe…</h1>
        <p className="text-[#64748B] mt-2 leading-relaxed">
          Your payment hasn&apos;t been confirmed yet. If you completed checkout, the confirmation usually arrives in a minute or two.
          You can leave this page — your account will upgrade automatically as soon as Stripe finalises the payment.
        </p>
        <p className="text-xs text-[#94A3B8] mt-4">Session: <code>{sessionId.slice(0, 24)}…</code></p>
        <div className="flex gap-3 justify-center mt-6">
          <Button onClick={() => { setAttempts(0); setPhase('polling'); }} variant="outline" data-testid="checkout-success-retry-poll">
            Check again
          </Button>
          <Link to="/dashboard">
            <Button data-testid="checkout-success-go-dashboard-anyway">Go to dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  // 'error'
  return (
    <div className="p-8 max-w-2xl mx-auto text-center" data-testid="checkout-success-error">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
      <h1 className="text-2xl font-extrabold text-[#020617]">We couldn&apos;t verify that session.</h1>
      <p className="text-[#64748B] mt-2">
        Either the session belongs to a different account, or it doesn&apos;t exist. If you were charged, contact support and we&apos;ll sort it.
      </p>
      <Button onClick={() => navigate('/pricing')} className="mt-6" data-testid="checkout-success-back-after-error">
        Back to pricing
      </Button>
    </div>
  );
};

export default CheckoutSuccess;
